"use client";

import { useState, useTransition } from "react";
import type { NewsletterSignupBlock as NewsletterSignupBlockProps } from "./schema";
import type { SiteId } from "@platform/schema";
import type { VariantId } from "@/lib/layout/templates";

/**
 * Newsletter / SMS opt-in — a lightweight subscribe form that routes into the
 * site's existing lead pipeline (`POST /api/leads`) with a `newsletter` intent.
 * Reuses the lead-form submit pattern (honeypot, mount-frozen attribution, a
 * `useTransition` submit, and a thank-you state on success). No new endpoint.
 *
 * Three layouts: `band` (conventional default — a centered band tinted with the
 * brand accent), `inline` (a quiet single-row strip on `--color-surface-1`), and
 * `split` (copy on the left, form on the right). All use the injected theme
 * tokens only — never the shadcn `--color-card` alias, which resolves dark on
 * the public light context.
 *
 * The `mode` decides which channel(s) the opt-in collects: `email`, `sms`
 * (phone), or `both`. The lead body carries whichever the dealer enabled; the
 * lead endpoint owns validation (this is a public, anonymous submission).
 */

type NewsletterMode = NewsletterSignupBlockProps["mode"];

interface SharedProps {
  heading?: string;
  subtext?: string;
  mode: NewsletterMode;
  consentText?: string;
  submitLabel?: string;
  siteId: SiteId;
}

export function NewsletterSignupBlock({
  heading,
  subtext,
  mode,
  consentText,
  submitLabel,
  siteId,
  variant,
}: NewsletterSignupBlockProps & { siteId: SiteId; variant?: VariantId }) {
  const shared: SharedProps = {
    heading,
    subtext,
    mode: mode ?? "email",
    consentText,
    submitLabel,
    siteId,
  };
  switch (variant) {
    case "inline":
      return <NewsletterInline {...shared} />;
    case "split":
      return <NewsletterSplit {...shared} />;
    default:
      return <NewsletterBand {...shared} />;
  }
}

// ---------------------------------------------------------------------------
// Attribution (mount-frozen, mirrors the lead form)
// ---------------------------------------------------------------------------

interface Attribution {
  utm: Record<string, string>;
  referrer: string | null;
}

function readAttribution(): Attribution {
  if (typeof window === "undefined") {
    return { utm: {}, referrer: null };
  }
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const v = params.get(k);
    if (v) {
      utm[k] = v;
    }
  }
  return { utm, referrer: document.referrer || null };
}

// ---------------------------------------------------------------------------
// Interactive form — shared across all three variants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "min-w-0 flex-1 rounded-(--radius-sm) border border-(--color-border-strong) bg-(--color-background) px-3 py-2.5 text-[13.5px] text-(--color-foreground) placeholder:text-(--color-foreground-faint)";

function NewsletterForm({
  mode,
  consentText,
  submitLabel,
  siteId,
  align = "center",
}: {
  mode: NewsletterMode;
  consentText?: string;
  submitLabel?: string;
  siteId: SiteId;
  align?: "center" | "start";
}) {
  const resolvedMode = mode ?? "email";
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [pending, startTransition] = useTransition();
  const [attribution] = useState<Attribution>(() => readAttribution());

  if (status === "ok") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="rounded-(--radius-sm) border border-(--color-border) bg-(--color-surface-2) px-5 py-4 text-center text-sm text-(--color-foreground)"
      >
        Thanks — you&apos;re on the list.
      </div>
    );
  }

  const showEmail = resolvedMode === "email" || resolvedMode === "both";
  const showPhone = resolvedMode === "sms" || resolvedMode === "both";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const email = showEmail ? String(fd.get("email") ?? "") : "";
        const phone = showPhone ? String(fd.get("phone") ?? "") : "";
        startTransition(async () => {
          try {
            const res = await fetch("/api/leads", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                siteId,
                intent: "newsletter",
                ...(showEmail ? { email } : {}),
                ...(showPhone ? { phone } : {}),
                source: attribution,
                honeypot: String(fd.get("hp_email") ?? ""),
              }),
            });
            if (res.ok) {
              setStatus("ok");
            } else {
              setStatus("error");
            }
          } catch {
            setStatus("error");
          }
        });
      }}
      className="flex flex-col gap-2.5"
    >
      <input type="hidden" name="siteId" value={siteId} />
      <input
        type="text"
        name="hp_email"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />
      <div className="flex flex-col gap-2.5 sm:flex-row">
        {showEmail ? (
          <input
            type="email"
            name="email"
            aria-label="Email address"
            placeholder="you@example.com"
            required
            autoComplete="email"
            className={INPUT_CLASS}
          />
        ) : null}
        {showPhone ? (
          <input
            type="tel"
            name="phone"
            aria-label="Phone number"
            placeholder="Mobile number"
            required
            autoComplete="tel"
            className={INPUT_CLASS}
          />
        ) : null}
        <button
          type="submit"
          disabled={pending}
          className="shrink-0 rounded-(--radius-sm) bg-(--color-brand-accent) px-5 py-2.5 text-sm font-semibold text-(--color-on-accent) transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {pending ? "Sending..." : (submitLabel ?? "Subscribe")}
        </button>
      </div>
      {consentText ? (
        <p
          className={[
            "text-xs leading-relaxed text-(--color-foreground-muted)",
            align === "center" ? "text-center" : "text-left",
          ].join(" ")}
        >
          {consentText}
        </p>
      ) : null}
      {status === "error" ? (
        <p
          className={[
            "text-xs text-red-500",
            align === "center" ? "text-center" : "text-left",
          ].join(" ")}
        >
          Something went wrong. Please try again.
        </p>
      ) : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

/** Conventional default — a centered band tinted with the brand accent. */
function NewsletterBand({ heading, subtext, mode, consentText, submitLabel, siteId }: SharedProps) {
  return (
    <section className="border-b border-(--color-border) bg-(--color-brand-accent)/8 px-6 py-14 md:px-10 md:py-20">
      <div className="mx-auto max-w-xl text-center">
        {heading ? (
          <h2 className="text-balance text-[clamp(1.5rem,3.5vw,2.25rem)] font-light leading-[1.1] tracking-[-0.02em] text-(--color-foreground)">
            {heading}
          </h2>
        ) : null}
        {subtext ? (
          <p className="mx-auto mt-3 max-w-prose text-base leading-relaxed text-(--color-foreground-muted)">
            {subtext}
          </p>
        ) : null}
        <div className="mx-auto mt-7 max-w-md">
          <NewsletterForm
            mode={mode}
            consentText={consentText}
            submitLabel={submitLabel}
            siteId={siteId}
            align="center"
          />
        </div>
      </div>
    </section>
  );
}

/** Inline — a quiet single-row strip on a low-contrast surface. */
function NewsletterInline({
  heading,
  subtext,
  mode,
  consentText,
  submitLabel,
  siteId,
}: SharedProps) {
  return (
    <section className="border-b border-(--color-border) bg-(--color-surface-1) px-6 py-10 md:px-10 md:py-12 lg:px-14">
      <div className="mx-auto flex max-w-4xl flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-10">
        <div className="md:max-w-sm">
          {heading ? (
            <h2 className="text-xl font-light tracking-[-0.02em] text-(--color-foreground)">
              {heading}
            </h2>
          ) : null}
          {subtext ? (
            <p className="mt-1 text-sm text-(--color-foreground-muted)">{subtext}</p>
          ) : null}
        </div>
        <div className="md:min-w-[20rem] md:flex-1">
          <NewsletterForm
            mode={mode}
            consentText={consentText}
            submitLabel={submitLabel}
            siteId={siteId}
            align="start"
          />
        </div>
      </div>
    </section>
  );
}

/** Split — editorial copy on the left, the form boxed on the right. */
function NewsletterSplit({
  heading,
  subtext,
  mode,
  consentText,
  submitLabel,
  siteId,
}: SharedProps) {
  return (
    <section className="border-b border-(--color-border) bg-(--color-surface-1) px-6 py-14 md:px-10 md:py-20 lg:px-14">
      <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-8 overflow-hidden rounded-(--radius-lg) border border-(--color-border) bg-(--color-background) p-7 shadow-(--shadow-card) md:grid-cols-[1fr_1fr] md:items-center md:gap-12 md:p-12">
        <div>
          <div className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-(--color-brand-accent)">
            Stay in touch
          </div>
          {heading ? (
            <h2 className="font-(--font-display) text-[clamp(1.5rem,3vw,2.25rem)] leading-[1.1] tracking-[-0.02em] text-balance text-(--color-foreground)">
              {heading}
            </h2>
          ) : null}
          {subtext ? (
            <p className="mt-3 max-w-prose text-[15px] leading-relaxed text-(--color-foreground-muted)">
              {subtext}
            </p>
          ) : null}
        </div>
        <div>
          <NewsletterForm
            mode={mode}
            consentText={consentText}
            submitLabel={submitLabel}
            siteId={siteId}
            align="start"
          />
        </div>
      </div>
    </section>
  );
}
